import { type Ctx, cyc, obox, R } from "./pixel";

/** Options a map object passes to its furniture painter. */
export interface DrawOpts {
  w?: number;
  h?: number;
  /** Bed is occupied: draw a patient with these colours. */
  pt?: boolean;
  hair?: string;
  skin?: string;
  sheet?: string;
}

export type DrawFn = (c: Ctx, px: number, py: number, o: DrawOpts) => void;

/** Placeholder for objects the level paints itself (in drawBelow/drawAbove). */
const drawnByLevel: DrawFn = () => undefined;

/** Furniture painters keyed by map object `type`; drawn once into the static background. */
export const DRAW: Record<string, DrawFn> = {
  bed(c, px, py, o) {
    R(c, "#6d7590", px + 1, py, 14, 3);
    R(c, "#8790a8", px + 1, py + 2, 14, 30);
    R(c, "#f4f4f6", px + 2, py + 3, 12, 28);
    R(c, "#ffffff", px + 3, py + 4, 10, 5);
    R(c, "#c9ccd8", px + 3, py + 9, 10, 1);
    if (o.pt) {
      R(c, o.hair ?? "#3a2a22", px + 5, py + 3, 6, 5);
      R(c, o.skin ?? "#f4dccd", px + 6, py + 5, 4, 3);
      R(c, "#1b1426", px + 6, py + 6, 1, 1);
      R(c, "#1b1426", px + 9, py + 6, 1, 1);
      R(c, o.sheet ?? "#a9c8e8", px + 2, py + 11, 12, 19);
      R(c, "#00000022", px + 2, py + 11, 12, 1);
    } else {
      R(c, "#d9dcea", px + 3, py + 20, 10, 6);
      R(c, "#c3c7da", px + 3, py + 23, 10, 1);
    }
  },
  exambed(c, px, py) {
    R(c, "#8790a8", px + 2, py + 2, 12, 28);
    R(c, "#7fb3a8", px + 3, py + 3, 10, 26);
    R(c, "#f4f4f6", px + 3, py + 3, 10, 6);
    R(c, "#5a6070", px + 3, py + 29, 2, 3);
    R(c, "#5a6070", px + 11, py + 29, 2, 3);
  },
  chair(c, px, py) {
    R(c, "#7a5236", px + 3, py + 3, 10, 3);
    R(c, "#9a6a46", px + 3, py + 6, 10, 6);
    R(c, "#5a3a26", px + 4, py + 12, 2, 3);
    R(c, "#5a3a26", px + 10, py + 12, 2, 3);
  },
  chairs(c, px, py, o) {
    for (let k = 0; k < (o.w ?? 1); k++) {
      const qx = px + k * 16;
      R(c, "#4a6a9a", qx + 1, py + 2, 14, 4);
      R(c, "#5f86bf", qx + 1, py + 6, 14, 6);
      R(c, "#3a3f52", qx + 2, py + 12, 2, 4);
      R(c, "#3a3f52", qx + 12, py + 12, 2, 4);
    }
  },
  us(c, px, py) {
    R(c, "#9aa0b2", px + 3, py + 12, 10, 3);
    R(c, "#c8ccd6", px + 4, py + 5, 8, 8);
    R(c, "#20263a", px + 5, py + 1, 6, 5);
    R(c, "#5f8fb8", px + 6, py + 2, 2, 2);
    R(c, "#7a8092", px + 3, py + 7, 10, 2);
  },
  sink(c, px, py) {
    R(c, "#c9ced9", px + 2, py + 3, 12, 10);
    R(c, "#f6f7fb", px + 3, py + 4, 10, 7);
    R(c, "#9aa0b2", px + 7, py + 1, 2, 4);
  },
  scrubsink(c, px, py) {
    R(c, "#9aa6b8", px, py + 2, 16, 4);
    R(c, "#c9ced9", px + 1, py + 5, 14, 9);
    R(c, "#eef1f7", px + 2, py + 6, 12, 6);
    R(c, "#7a8092", px + 7, py + 2, 2, 6);
    R(c, "#5f8fb8", px + 3, py, 2, 3);
  },
  cart(c, px, py) {
    R(c, "#9b2f2a", px + 2, py + 2, 12, 12);
    R(c, "#c8463e", px + 2, py + 2, 12, 3);
    for (let k = 0; k < 3; k++) {
      R(c, "#7e231f", px + 3, py + 6 + k * 3, 10, 1);
      R(c, "#e0d6c0", px + 7, py + 7 + k * 3, 2, 1);
    }
    R(c, "#1b1426", px + 3, py + 14, 2, 2);
    R(c, "#1b1426", px + 11, py + 14, 2, 2);
  },
  shelf(c, px, py, o) {
    for (let k = 0; k < (o.w ?? 1); k++) {
      const qx = px + k * 16;
      R(c, "#6b4a34", qx, py + 2, 16, 13);
      R(c, "#8a5f40", qx, py + 8, 16, 1);
      R(c, cyc(["#e0a3a3", "#a3c2e0", "#e8d48a"], k), qx + 2, py + 3, 4, 5);
      R(c, cyc(["#a3e0b8", "#e0a3cf", "#a3c2e0"], k), qx + 8, py + 4, 5, 4);
      R(c, "#d9dcea", qx + 3, py + 10, 9, 4);
    }
  },
  counter(c, px, py, o) {
    for (let k = 0; k < (o.w ?? 1); k++) {
      const qx = px + k * 16;
      R(c, "#8e6238", qx, py + 5, 16, 11);
      R(c, "#b98552", qx, py + 2, 16, 5);
      R(c, "#7a5230", qx, py + 15, 16, 1);
    }
  },
  phone(c, px, py) {
    R(c, "#1f1f2a", px + 3, py + 3, 10, 3);
    R(c, "#3a3a4c", px + 4, py + 2, 8, 2);
  },
  chart(c, px, py) {
    R(c, "#3b5fa8", px + 3, py + 1, 10, 6);
    R(c, "#f4f4f6", px + 5, py + 2, 7, 4);
  },
  computer(c, px, py) {
    R(c, "#20263a", px + 3, py - 2, 10, 7);
    R(c, "#5f8fb8", px + 4, py - 1, 8, 5);
    R(c, "#3a3f52", px + 7, py + 5, 2, 2);
    R(c, "#d9dcea", px + 3, py + 6, 10, 2);
  },
  desk(c, px, py, o) {
    for (let k = 0; k < (o.w ?? 1); k++) {
      const qx = px + k * 16;
      R(c, "#b98552", qx, py + 3, 16, 6);
      R(c, "#8e6238", qx, py + 9, 16, 6);
      R(c, "#6b4a34", qx + (k === 0 ? 1 : 13), py + 9, 2, 7);
    }
  },
  bench(c, px, py, o) {
    R(c, "#9a6a46", px + 1, py + 5, (o.w ?? 1) * 16 - 2, 6);
    R(c, "#7a5236", px + 1, py + 11, (o.w ?? 1) * 16 - 2, 2);
    R(c, "#5a3a26", px + 3, py + 13, 2, 3);
    R(c, "#5a3a26", px + (o.w ?? 1) * 16 - 5, py + 13, 2, 3);
  },
  plant(c, px, py) {
    R(c, "#8a5f40", px + 5, py + 10, 6, 5);
    R(c, "#3d8a4f", px + 3, py + 3, 10, 8);
    R(c, "#5cb06c", px + 5, py + 2, 4, 5);
    R(c, "#2c6b3b", px + 8, py + 6, 4, 3);
  },
  recliner(c, px, py) {
    R(c, "#b7708a", px + 2, py + 2, 12, 13);
    R(c, "#d48aa6", px + 3, py + 3, 10, 6);
    R(c, "#9c5a74", px + 2, py + 11, 12, 3);
  },
  nst(c, px, py) {
    R(c, "#d9dcea", px + 3, py + 3, 10, 11);
    R(c, "#20263a", px + 4, py + 4, 8, 4);
    R(c, "#f4f4f6", px + 4, py + 9, 8, 4);
    R(c, "#e05a6a", px + 5, py + 10, 6, 1);
  },
  bp(c, px, py) {
    R(c, "#d9dcea", px + 3, py + 2, 10, 13);
    R(c, "#20263a", px + 4, py + 3, 8, 4);
    R(c, "#7fe08a", px + 5, py + 4, 6, 1);
    R(c, "#5f86bf", px + 5, py + 9, 6, 4);
    R(c, "#f4f4f6", px + 12, py + 6, 2, 5);
  },
  scale(c, px, py) {
    R(c, "#9aa0b2", px + 3, py + 10, 10, 4);
    R(c, "#c8ccd6", px + 7, py + 2, 2, 9);
    R(c, "#20263a", px + 5, py + 1, 6, 3);
  },
  water(c, px, py) {
    R(c, "#d9dcea", px + 4, py + 4, 8, 11);
    R(c, "#7fb3e8", px + 5, py, 6, 5);
    R(c, "#e05a6a", px + 5, py + 8, 2, 2);
    R(c, "#5f8fb8", px + 9, py + 8, 2, 2);
  },
  wcdoor(c, px, py) {
    R(c, "#6b4a34", px + 2, py + 1, 12, 15);
    R(c, "#8a5f40", px + 3, py + 2, 10, 13);
    R(c, "#5f8fb8", px + 5, py + 5, 6, 4);
    R(c, "#fff", px + 7, py + 6, 2, 2);
  },
  board(c, px, py) {
    R(c, "#1b1426", px + 1, py + 3, 30, 9);
    R(c, "#ff5a5a", px + 3, py + 5, 5, 5);
    R(c, "#ff5a5a", px + 10, py + 5, 5, 5);
    R(c, "#ffd447", px + 20, py + 5, 8, 5);
  },
  ortable: drawnByLevel,
  anesth(c, px, py) {
    R(c, "#6d7a90", px + 2, py + 2, 12, 13);
    R(c, "#8a97ad", px + 3, py + 3, 10, 5);
    R(c, "#20263a", px + 4, py + 4, 8, 3);
    R(c, "#5a6070", px + 3, py + 10, 10, 2);
    R(c, "#e8e8ec", px + 12, py + 9, 3, 5);
  },
  tower(c, px, py) {
    R(c, "#3a3f52", px + 3, py + 2, 10, 28);
    R(c, "#20263a", px + 2, py, 12, 8);
    R(c, "#7a2a2a", px + 3, py + 1, 10, 6);
    R(c, "#5a6070", px + 4, py + 12, 8, 2);
    R(c, "#5a6070", px + 4, py + 18, 8, 2);
  },
  backtable(c, px, py, o) {
    R(c, "#2f5f9e", px, py + 2, (o.w ?? 1) * 16, 12);
    R(c, "#3f72b5", px, py + 2, (o.w ?? 1) * 16, 3);
    for (let k = 0; k < 6; k++) R(c, "#d9dcea", px + 3 + k * 5, py + 7, 3, 1);
    R(c, "#d9dcea", px + 6, py + 10, 8, 1);
  },
  mayo(c, px, py) {
    R(c, "#3f72b5", px + 1, py + 3, 14, 6);
    R(c, "#d9dcea", px + 3, py + 5, 2, 2);
    R(c, "#d9dcea", px + 7, py + 5, 5, 1);
    R(c, "#7a8092", px + 7, py + 9, 2, 6);
  },
  kick(c, px, py) {
    R(c, "#9aa0b2", px + 3, py + 4, 10, 10);
    R(c, "#c8ccd6", px + 3, py + 4, 10, 2);
    R(c, "#f4f4f6", px + 5, py + 6, 6, 3);
    R(c, "#c0394a", px + 6, py + 7, 3, 2);
    R(c, "#1b1426", px + 4, py + 14, 2, 2);
    R(c, "#1b1426", px + 10, py + 14, 2, 2);
  },
  whiteboard(c, px, py) {
    R(c, "#9aa0b2", px + 2, py + 1, 12, 13);
    R(c, "#ffffff", px + 3, py + 2, 10, 10);
    R(c, "#3b5fa8", px + 4, py + 4, 7, 1);
    R(c, "#3b5fa8", px + 4, py + 7, 5, 1);
    R(c, "#c0394a", px + 4, py + 10, 6, 1);
  },
  suction(c, px, py) {
    R(c, "#7a8092", px + 7, py + 4, 2, 11);
    R(c, "#d9e6f0", px + 4, py + 2, 8, 6);
    R(c, "#c0394a", px + 5, py + 5, 6, 2);
  },
  esu(c, px, py) {
    R(c, "#d9dcea", px + 2, py + 4, 12, 9);
    R(c, "#20263a", px + 3, py + 5, 6, 3);
    R(c, "#ffd447", px + 10, py + 6, 3, 2);
    R(c, "#5a6070", px + 2, py + 13, 12, 2);
  },
  vending(c, px, py) {
    R(c, "#c0394a", px + 2, py, 12, 16);
    R(c, "#20263a", px + 3, py + 2, 7, 9);
    R(c, "#ffd447", px + 4, py + 3, 2, 2);
    R(c, "#7fe08a", px + 7, py + 3, 2, 2);
    R(c, "#7fb3e8", px + 4, py + 7, 2, 2);
    R(c, "#d9dcea", px + 11, py + 4, 2, 5);
  },
  shelf2(c, px, py) {
    R(c, "#9aa0b2", px + 1, py + 1, 14, 14);
    R(c, "#2f5f9e", px + 2, py + 2, 12, 4);
    R(c, "#7fb3d5", px + 2, py + 8, 12, 4);
  },
  clock: drawnByLevel,
  monitorStand: drawnByLevel,
  stand(c, px, py) {
    obox(c, "#b98552", px + 2, py + 4, 12, 10);
    R(c, "#8e6238", px + 2, py + 9, 12, 1);
    R(c, "#e2c27a", px + 7, py + 11, 2, 1);
  },
};
